import "./QuestionPage.css";

import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import React, { useEffect, useState } from "react";
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
    createNewSurvey,
    getAllQuestionTypes,
    updateSurvey,
} from "../../services/survey/survey.service";

import { CSS } from "@dnd-kit/utilities";
import ModalComponent from "../common/ModalComponent/ModalComponent";
import box from "../../assets/box.png";
import crossIcon from "../../assets/cross.png";
import pencil from "../../assets/pencil.png";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import validateFields from "../../utils/ValidationInput";
import { useTranslation } from "react-i18next";
import renderWithLinks from "../../utils/linkGenerator";

const QuestionPage = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const { surveyformData, from, surveyId, survey, storedSurveyDetails, isEdit, languageId, isChildPresent } = location.state || {};
    const [loading, setLoading] = useState(false);
    const [questionTypeList, setQuestionTypeList] = useState([]);
    const [questionDetails, setQuestionDetails] = useState({});
    const [selectedQueType, setSelectedQueType] = useState({});
    const [sectionHeadingList, setSectionHeadingList] = useState([]);
    const [surveyData, setSurveyData] = useState([]);
    const [sectionHeading, setSectionHeading] = useState("");
    const [newSectionHeading, setNewSectionHeading] = useState("");
    const [questionText, setQuestionText] = useState("");
    const [questionType, setQuestionType] = useState("");
    const [isRequired, setIsRequired] = useState(false);
    const [options, setOptions] = useState(["", ""]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingSectionIndex, setEditingSectionIndex] = useState(null);
    const [editingQuestionId, setEditingQuestionId] = useState(null);
    const [isWithinSection, setIsWithinSection] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [deleteSectionIndex, setDeleteSectionIndex] = useState(null);
    const [deleteQuestionId, setDeleteQuestionId] = useState(null);
    const [deleteQuestionText, setDeleteQuestionText] = useState(null);
    const [sortedSurveyData, setSortedSurveyData] = useState([]);
    const [questionNumberMap, setQuestionNumberMap] = useState({});
    const sensors = useSensors(useSensor(PointerSensor));
    const navigate = useNavigate();
    const [openSections, setOpenSections] = useState({});

    useEffect(() => {
        fetchQuestionsTypes();
    }, []);

    useEffect(() => {
        if (surveyformData && surveyId) {
            setSurveyData(surveyformData || []);
            const sectionHeadings = surveyformData
                ?.filter(
                    (item) =>
                        item.section &&
                        item.section.trim() !== "" &&
                        item.section !== "No Section"
                )
                .map((item) => ({
                    ...item,
                    label: item?.section,
                    value: item?.section,
                }));
            setSectionHeadingList(sectionHeadings);
        }
    }, [surveyformData]);

    useEffect(() => {
        const sortedSections = [
            ...surveyData.filter(
                (section) => section.section && section.section.trim() !== ""
            ),
            ...surveyData.filter(
                (section) => !section.section || section.section.trim() === ""
            ),
        ];

        // Assign unique numbers to each question
        let uniqueNumber = 1;
        const numMap = {};
        sortedSections.forEach((section) => {
            section.questions.forEach((question) => {
                numMap[question.id] = uniqueNumber++;
            });
        });

        setSortedSurveyData(sortedSections);
        setQuestionNumberMap(numMap);
    }, [surveyData]);

    const fetchQuestionsTypes = () => {
    setLoading(true);
    getAllQuestionTypes(languageId).then((response) => {
        if (response !== null) {
            setLoading(false);
            const questionTypes = response?.data?.map((obj) => ({
                ...obj,
                label: t(`question_Type.${obj.code}`), // Use i18n key
                value: obj?.id,
            }));
            setQuestionTypeList(questionTypes);
        } else {
            setLoading(false);
        }
    });
};
    const handleFieldChange = (name, value, index = null) => {
        toast.dismiss();
        if (name === "sectionHeading" && value.length > 30) {
            toast.error(t("sectionHeadingExceedsMaxLengthError", { number: 30 }), {
                position: "top-center",
            });
            return;
        }
        if (name === "questionText" && value.length > 255) {
            toast.error(t("questionTextExceedsMaxLengthError", { number: 255 }), {
                position: "top-center",
            });
            return;
        }
        if (name === "option" && value.length > 255) {
            toast.error(t("optionExceedsMaxLengthError", { number: 255 }), {
                position: "top-center",
            });
            return;
        }

        // Update state accordingly
        if (name === "sectionHeading") {
            setNewSectionHeading(value);
        } else if (name === "questionText") {
            setQuestionText(value);
        } else if (name === "option" && index !== null) {
            const updatedOptions = [...options];
            updatedOptions[index] = value;
            setOptions(updatedOptions);
        }
    };

    const clearFields = () => {
        // Reset form fields
        setSectionHeading("");
        setNewSectionHeading("");
        setQuestionText("");
        setQuestionType("");
        setIsRequired(false);
        setOptions([]);
        // setOptions(["", ""]);
    };

    const trimText = (text) => {
        return text.trim().replace(/\s+/g, " ");
    };

    const handleAddQuestion = () => {
        let optionsList = [];
        // let questionsList = [...allQuestions];
        let serveyDetailsList = [...sortedSurveyData];

        let requiredFields = [
            { label: t("questionType"), key: "Question Type", type: "text", value: questionType },
            {
                label: t("questionText"),
                key: "Question Text",
                type: "",
                value: trimText(questionText),
                charLength: 255,
            },
        ];
        if (isWithinSection === true && sectionHeading === "Others") {
            requiredFields.push({
                label: t("sectionHeading"),
                key: "Section Heading",
                type: "text",
                value: newSectionHeading,
                charLength: 30,
            });
        }

        // If MCQ is selected, validate options
        if (
            selectedQueType?.code === "MCQ" ||
            selectedQueType?.code === "SCQ"
        ) {
            // Check if any option is empty
            const hasEmptyOption = options.some(
                (option) => option.trim() === ""
            );
            if (hasEmptyOption) {
                toast.error(t("fillAllOptionsError"), {
                    position: "top-center",
                    theme: "dark",
                });
                return;
            }

            // Check if any option exceeds 255 characters
            const hasLongOption = options.some((option) => option.length > 255);
            if (hasLongOption) {
                toast.error(t("optionExceedsMaxLengthError", { number: 255 }), {
                    position: "top-center",
                    theme: "dark",
                });
                return;
            }
        }

        const isValid = validateFields(
            { questionText, questionType },
            requiredFields,
            "byValue"
        );
        if (!isValid) {
            setLoading(false);
            return;
        }

        if (
            selectedQueType?.code === "MCQ" ||
            selectedQueType?.code === "SCQ"
        ) {
            optionsList = options;
        } else if (selectedQueType?.code === "YesNo") {
            optionsList = ["Yes", "No"];
        } else if (
            selectedQueType?.code === "FreeText" ||
            selectedQueType?.code === "OneWord"
        ) {
            optionsList = [];
        } else if (selectedQueType?.code === "TrueFalse") {
            optionsList = ["True", "False"];
        }

        const finalSectionName =
            sectionHeading === "Others" ? newSectionHeading : sectionHeading;

        if (isEditing === false) {
            // Add new question
            const newQuestion = {
                id: Date.now(),
                isNewQuestion: true, // Flag to identify new questions
                isSectionRequired: isWithinSection,
                sectionTitle:
                    sectionHeading === "Others"
                        ? newSectionHeading
                        : sectionHeading,
                isMandatory: isRequired,
                questionText: questionText,
                surveyQuestionTypeId: questionType,
                options: optionsList,
                selectedQueType: selectedQueType,
                sectionHeading: sectionHeading,
            };

            if (isWithinSection === true) {
                // Check if the section already exists
                const existingSection = serveyDetailsList?.find(
                    (sectionObj) => sectionObj.section === finalSectionName
                );

                if (existingSection) {
                    // Add question to existing section
                    existingSection.questions.push(newQuestion);
                } else {
                    // Create a new section with the question
                    serveyDetailsList.push({
                        section:
                            finalSectionName === "" ? "" : finalSectionName,
                        questions: [newQuestion],
                    });
                }
            } else {
                const noSectionName = "";
                // Check if "No Section" already exists
                const existingNoSection = serveyDetailsList.find(
                    (sectionObj) => sectionObj.section === noSectionName
                );

                if (existingNoSection) {
                    // Append to existing "No Section"
                    existingNoSection.questions.push(newQuestion);
                } else {
                    // Create new "No Section" entry
                    serveyDetailsList.push({
                        section: noSectionName,
                        questions: [newQuestion],
                    });
                }
            }

            setSurveyData(serveyDetailsList);
            // setSectionHeadingList((item) => ({
            //   ...item,
            //   [finalSectionName]: true,
            // }))

            // List for section headings
            const sectionHeadings = serveyDetailsList
                ?.filter(
                    (item) =>
                        item.section &&
                        item.section.trim() !== "" &&
                        item.section !== "No Section"
                )
                .map((item) => ({
                    ...item,
                    label: item?.section,
                    value: item?.section,
                }));
            setSectionHeadingList(sectionHeadings);
            toast.success(t("questionAddedSuccessfully"), {
                position: "top-center",
            });
        } else {
            // Update existing question
            const sectionIndex = editingSectionIndex;
            const questionIndex = serveyDetailsList[
                sectionIndex
            ].questions.findIndex(
                (question) => question.id === editingQuestionId
            );

            const updatedQuestion = {
                id: questionDetails.id,
                isSectionRequired: isWithinSection,
                sectionTitle: finalSectionName,
                isMandatory: isRequired,
                questionText: questionText,
                surveyQuestionTypeId: questionType,
                options: optionsList,
                selectedQueType: selectedQueType,
                sectionHeading: sectionHeading,
            };

            // Determine if section type or section name changed
            const oldSectionName =
                serveyDetailsList[sectionIndex].section || "";
            const newSectionName = isWithinSection ? finalSectionName : "";

            if (oldSectionName === newSectionName) {
                // Section type/name did not change, update in place
                serveyDetailsList[sectionIndex].questions[questionIndex] =
                    updatedQuestion;
            } else {
                // Section type or section name changed
                // 1. Remove from old section
                if (questionIndex > -1) {
                    serveyDetailsList[sectionIndex].questions.splice(
                        questionIndex,
                        1
                    );
                }
                // 2. Remove the section if it becomes empty
                if (serveyDetailsList[sectionIndex].questions.length === 0) {
                    serveyDetailsList.splice(sectionIndex, 1);
                }
                // 3. Add to new section (or "No Section")
                let targetSection = serveyDetailsList.find(
                    (sectionObj) =>
                        (sectionObj.section || "") === newSectionName
                );
                if (targetSection) {
                    targetSection.questions.push(updatedQuestion);
                } else {
                    serveyDetailsList.push({
                        section: newSectionName,
                        questions: [updatedQuestion],
                    });
                }
            }

            setSurveyData(serveyDetailsList);
            setIsEditing(false);
            setEditingSectionIndex(null);
            setEditingQuestionId(null);
            toast.success(t("questionUpdatedSuccessfully"), {
                position: "top-center",
            });
        }

        // Update section heading list
        const sectionHeadings = serveyDetailsList
            .filter(
                (item) =>
                    item.section &&
                    item.section.trim() !== "" &&
                    item.section !== "No Section"
            )
            .map((item) => ({
                ...item,
                label: item?.section,
                value: item?.section,
            }));
        setSectionHeadingList(sectionHeadings);

        clearFields();
    };

    const handleDeleteQuestion = (sectionIndex, question) => {
        setShowModal(true);
        setDeleteSectionIndex(sectionIndex);
        setDeleteQuestionId(question?.id);
        setDeleteQuestionText(question?.questionText);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleConfirmDelete = () => {
        const updatedSurveyData = [...surveyData];

        updatedSurveyData[deleteSectionIndex].questions = updatedSurveyData[
            deleteSectionIndex
        ].questions.filter((question) => question.id !== deleteQuestionId);

        // If the section has no more questions, remove the section
        if (updatedSurveyData[deleteSectionIndex].questions.length === 0) {
            updatedSurveyData.splice(deleteSectionIndex, 1);
            toast.success(t("questionDeletedSuccessfully"), {
                position: "top-center",
            });
        }
        setSurveyData(updatedSurveyData);
        setIsEditing(false);
        setEditingSectionIndex(null);
        setEditingQuestionId(null);
        clearFields();
        setIsWithinSection(true);
        setIsEditing(false);
        setShowModal(false);
    };

    const openModal = (question, section, sectionIndex) => {
        setSectionHeading(question?.sectionHeading);
        setQuestionText(question?.questionText);
        setQuestionType(question?.selectedQueType?.id);
        setQuestionDetails(question);
        setIsRequired(question?.isMandatory);
        // setOptions(question?.options || []);
        setNewSectionHeading(question?.sectionTitle);
        setIsWithinSection(question?.isSectionRequired);
        setIsEditing(true);
        setEditingSectionIndex(sectionIndex);
        setEditingQuestionId(question?.id);
        // Set options based on question type
        setSelectedQueType(question?.selectedQueType || {});
        switch (question?.selectedQueType?.code) {
            case "MCQ":
            case "SCQ":
                setOptions(question?.options);
                break;

            case "YesNo":
            case "TrueFalse":
            case "OneWord":
            case "FreeText":
                setOptions([]);
                break;

            default:
                setOptions(question?.options || []);
                break;
        }
    };

    const handleCancel = () => {
        navigate("/admin/survey/survey-page", {
            state: {
                storedSurveyData:
                    isEdit === true ? storedSurveyDetails : surveyformData,
                from: "/admin/survey/survey-page/question-page",
            },
        });
    };

    const handleSave = async () => {
        setLoading(true);
        const data = survey?.surveyId ? survey : surveyformData;
        const allQuestions = surveyData.flatMap((section) => section.questions);
        let payload = {
            title: data?.surveyTitle,
            surveyLayoutId: data?.layoutDetails?.id,
            imageId: data?.imageDetails === "" ? null : data?.imageDetails?.id,
            requiredConfirmation: data?.isPrivacyNoticeChecked,
            languageMasterId: languageId,
            description: data?.description,
            privacyNote: data?.privacyNotice !== "" ? data?.privacyNotice?.replace(/<[^>]*>/g, '') : "",
            questions: allQuestions.map((question) => {
                const questionPayload = {
                    isSectionRequired: question.isSectionRequired,
                    sectionTitle: question.sectionTitle,
                    isMandatory: question.isMandatory,
                    questionText: question.questionText,
                    surveyQuestionTypeId: question.surveyQuestionTypeId,
                    options:
                        question?.selectedQueType?.code === "FreeText" ||
                            question?.selectedQueType?.code === "OneWord"
                            ? null
                            : question.options,
                };
                
                // Include questionId only when updating
                if (surveyId) {
                    // For new questions added during update, pass questionId as null
                    const isNewQuestion = question.isNewQuestion;
                    questionPayload.questionId = isNewQuestion ? null : question.id;
                }
                return questionPayload;
            }),
        };
        if (surveyId) {
            await updateSurvey(payload, surveyId).then((response) => {
                if (response === null) {
                    setLoading(false);
                } else {
                    setLoading(false);
                    navigate("/admin/survey");
                }
            });
        } else {
            await createNewSurvey(payload).then((response) => {
                if (response === null) {
                    setLoading(false);
                } else {
                    setLoading(false);
                    navigate("/admin/survey");
                }
            });
        }
    };

    const onChangeQueType = (value) => {
        if (typeof value !== "number") value = parseInt(value);
        setQuestionType(value);
        let questionType = questionTypeList?.find(
            (option) => option.id === value
        );
        if (questionType?.code === "MCQ" || questionType?.code === "SCQ") {
            setOptions(["", ""]);
        }
        setSelectedQueType(questionType);
    };

    const onChangeSection = (value) => {
        setIsWithinSection(value);
        if (isEditing === false) clearFields();
    };

    const handleDragEnd = (event, sectionIndex) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const updated = [...sortedSurveyData];
        const section = updated[sectionIndex];
        const oldIndex = section.questions.findIndex((q) => q.id === active.id);
        const newIndex = section.questions.findIndex((q) => q.id === over.id);

        section.questions = arrayMove(section.questions, oldIndex, newIndex);
        setSortedSurveyData(updated);
    };

    useEffect(() => {
        if (surveyData && surveyData.length > 0) {
            setOpenSections((prev) => {
                const updated = { ...prev };
                surveyData.forEach((_, index) => {
                    // Preserve existing state, open only new sections
                    if (!(index in updated)) {
                        updated[index] = true; // open new section by default
                    }
                });
                return updated;
            });
        }
    }, [surveyData]);

    const toggleSection = (index) => {
        setOpenSections((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const renderQuestion = (
        question,
        index,
        sectionIndex,
        dragListeners,
        dragAttributes
    ) => (
        <div className="survey-question-container">
            {isChildPresent === false && 
                <div
                    className="dragger-dots"
                    {...dragAttributes}
                    {...dragListeners}
                >
                    ⋮⋮
                </div>
            }
            
            <div style={{ flex: 1 }}>
                <p className="survey-question-text" style={{ margin: 0 }}>
                    <span>{index + 1}.</span>
                    {question.isMandatory && (
                        <span className="survey-required">*</span>
                    )}
                    <span style={{ paddingLeft: "5px" }}>
                        {renderWithLinks(question.questionText)}
                    </span>
                </p>

                {["MCQ", "SCQ", "YesNo", "TrueFalse"].includes(
                    question.selectedQueType?.code
                ) && (
                        <div className="survey-options-container">
                            <div className="survey-options">
                                {question.options.map((option, i) => (
                                    <label key={i} className="survey-option-label">
                                        <input
                                            type={
                                                question.selectedQueType.code ===
                                                    "MCQ"
                                                    ? "checkbox"
                                                    : "radio"
                                            }
                                            name={`question-${question.id}`}
                                            value={option}
                                            disabled
                                        />
                                        {t(option)}
                                    </label>
                                ))}
                            </div>
                            <div className="survey-icons">
                                <div
                                    className="composite-icon"
                                    onClick={() =>
                                        openModal(
                                            question,
                                            sortedSurveyData[sectionIndex],
                                            sectionIndex
                                        )
                                    }
                                >
                                    <img src={box} alt="Box" className="box-icon" />
                                    <img
                                        src={pencil}
                                        alt="Pencil"
                                        className="pencil-icon"
                                    />
                                </div>
                                {isChildPresent === false && (
                                    <img
                                        src={crossIcon}
                                        alt="Delete"
                                        className="survey-icon"
                                        onClick={() =>
                                            handleDeleteQuestion(sectionIndex, question)
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    )}

                {(question.selectedQueType?.code === "FreeText" ||
                    question.selectedQueType?.code === "OneWord") && (
                        <div className="survey-options-container">
                            <input
                                type="text"
                                className="text-answer"
                                placeholder={t("typeAnswer")}
                                readOnly
                            />
                            <div className="survey-icons">
                                <div
                                    className="composite-icon"
                                    onClick={() =>
                                        openModal(
                                            question,
                                            sortedSurveyData[sectionIndex],
                                            sectionIndex
                                        )
                                    }
                                >
                                    <img src={box} alt="Box" className="box-icon" />
                                    <img
                                        src={pencil}
                                        alt="Pencil"
                                        className="pencil-icon"
                                    />
                                </div>
                                {isChildPresent === false && (
                                    <img
                                        src={crossIcon}
                                        alt="Delete"
                                        className="survey-icon"
                                        onClick={() =>
                                            handleDeleteQuestion(sectionIndex, question)
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
    const SortableQuestion = ({
        question,
        index,
        sectionIndex,
        renderQuestion,
    }) => {
        const {
            setNodeRef,
            attributes,
            listeners,
            transform,
            transition,
            isDragging,
        } = useSortable({ id: question.id });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
        };

        return (
            <div ref={setNodeRef} style={style}>
                {renderQuestion(
                    question,
                    index,
                    sectionIndex,
                    listeners,
                    attributes
                )}
            </div>
        );
    };

    return (
        <div className="ques-page-survey-review-container col-sm-12">
            {/* Survey Details Section */}
            <div className="ques-survey-details ">
                <h3>{t("questionDetails")}</h3>
                <hr />
                <div className="ques-survey-details-scrollable">
                    <div>
                        <label>
                            <input
                                type="radio"
                                name="section"
                                // value={isWithinSection}
                                checked={isWithinSection === true}
                                onChange={() => onChangeSection(true)}
                                disabled={isChildPresent}
                                style={isChildPresent ? {opacity: 0.5, pointerEvents: 'none'} : {}}
                            />{" "}
                            {t("questionsWithinSection")}
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="section"
                                // value={isWithinSection}
                                checked={isWithinSection === false}
                                onChange={() => onChangeSection(false)}
                                disabled={isChildPresent}
                                style={isChildPresent ? {opacity: 0.5, pointerEvents: 'none'} : {}}
                            />{" "}
                            {t("questionsWithoutSection")}
                        </label>
                    </div>

                    {isWithinSection && (
                        <>
                            <label>{t("selectSectionHeading")}</label>
                            <select
                                value={sectionHeading}
                                onChange={(e) => {
                                    setSectionHeading(e.target.value);
                                }}
                                className="select-dropdown"
                                disabled={isChildPresent}
                                style={isChildPresent ? {opacity: 0.5, pointerEvents: 'none'} : {}}
                            >
                                <option value="">{t("selectSection")}</option>
                                {sectionHeadingList.map((section, index) => (
                                    <option key={index} value={section.label}>
                                        {section.label}
                                    </option>
                                ))}
                                <option value="Others">
                                    {t("addSectionHeading")}
                                </option>
                            </select>

                            {sectionHeading === "Others" && (
                                <div>
                                    <label>{t("sectionHeading")}</label>
                                    <input
                                        type="text"
                                        value={newSectionHeading}
                                        onChange={(e) =>
                                            handleFieldChange(
                                                "sectionHeading",
                                                e.target.value
                                            )
                                        }
                                        // onChange={(e) => setNewSectionHeading(e.target.value)}
                                        placeholder={t("enterSectionHeading")}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    <label>{t("selectQuestionType")}</label>
                    <select
                        value={questionType}
                        onChange={(e) => onChangeQueType(e.target.value)}
                        className="select-dropdown"
                        disabled={isChildPresent}
                        style={isChildPresent ? {opacity: 0.5, pointerEvents: 'none'} : {}}
                    >
                        <option value="">{t("selectQuestionType")}</option>
                        {questionTypeList?.map((option, index) => (
                            <option
                                key={index}
                                value={option.value}
                                disabled={
                                    option.code === "MOQ" ||
                                    option.code === "Rating"
                                }
                            >
                                {/* {option.label} */}
                                {t(`question_Type.${option.code}`)}
                            </option>
                        ))}
                    </select>

                    <label>{t("question")}</label>
                    <textarea
                        rows={7}
                        className="ques-textarea"
                        value={questionText}
                        // onChange={(e) => setQuestionText(e.target.value)}
                        onChange={(e) =>
                            handleFieldChange("questionText", e.target.value)
                        }
                        placeholder={t("question")}
                    />

                    {(selectedQueType?.code === "MCQ" ||
                        selectedQueType?.code === "SCQ") && (
                            <>
                                {options.map((option, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <input
                                            type="text"
                                            value={option}
                                            // onChange={(e) => {
                                            //   const updatedOptions = [...options];
                                            //   updatedOptions[index] = e.target.value;
                                            //   setOptions(updatedOptions);
                                            // }}
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    "option",
                                                    e.target.value,
                                                    index
                                                )
                                            }
                                            placeholder={`Option ${index + 1}`}
                                            disabled={isChildPresent}
                                            style={isChildPresent ? {opacity: 0.5, pointerEvents: 'none'} : {}}
                                        />
                                        <button
                                            type="button"
                                            className="ques-remove-option-btn"
                                            onClick={() => {
                                                const updatedOptions =
                                                    options.filter(
                                                        (_, i) => i !== index
                                                    );
                                                setOptions(updatedOptions);
                                            }}
                                            disabled={isChildPresent}
                                            style={isChildPresent ? {opacity: 0.5, pointerEvents: 'none'} : {}}
                                        >
                                            X
                                        </button>
                                    </div>
                                ))}
                                {options.length < 15 && (
                                    <button
                                        className="ques-add-option-btn "
                                        type="button"
                                        onClick={() => setOptions([...options, ""])}
                                        style={isChildPresent ? {
                                            opacity: 0.5, 
                                            pointerEvents: 'none', 
                                            marginBottom: "30px",
                                            marginTop: "10px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-start" 
                                        } : {
                                            marginBottom: "30px",
                                            marginTop: "10px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-start",
                                        }}
                                        disabled={isChildPresent}
                                    >
                                        <svg
                                            width="15"
                                            height="15"
                                            viewBox="0 0 22 22"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.3333 16.9997H11.6666V11.6663H16.9999V10.333H11.6666V4.99967H10.3333V10.333H4.99992V11.6663H10.3333V16.9997ZM0.333252 21.6663V0.333008H21.6666V21.6663H0.333252ZM1.66659 20.333H20.3333V1.66634H1.66659V20.333Z"
                                                fill="#c80000"
                                            />
                                        </svg>
                                        {t("addOption")}
                                    </button>
                                )}
                                {options.length >= 15 && (
                                    <p style={{ color: "red", marginTop: "10px" }}>
                                        {t("maxOptionsAllowed")}
                                    </p>
                                )}
                            </>
                        )}

                    <label>
                        <input
                            type="checkbox"
                            checked={isRequired}
                            onChange={() => setIsRequired(!isRequired)}
                            disabled={isChildPresent}
                            style={isChildPresent ? {opacity: 0.5, pointerEvents: 'none'} : {}}
                        />{" "}
                        {t("mandatory")}
                    </label>
                </div>
                <div className="fixed-bottom-buttons ">
                    {isChildPresent === false ?
                        <button
                            className="ques-add-question-button"
                            onClick={handleAddQuestion}
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 22 22"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M10.3333 16.9997H11.6666V11.6663H16.9999V10.333H11.6666V4.99967H10.3333V10.333H4.99992V11.6663H10.3333V16.9997ZM0.333252 21.6663V0.333008H21.6666V21.6663H0.333252ZM1.66659 20.333H20.3333V1.66634H1.66659V20.333Z"
                                    fill="#c80000"
                                />
                            </svg>
                            {isEditing ? t("updateQuestion") : t("addQuestion")}
                        </button>
                    : 
                        <button
                            className="ques-add-question-button"
                            onClick={handleAddQuestion}
                            disabled={!isEditing}
                            style={{ opacity: !isEditing && 0.5 }}
                        >
                            <svg
                            width="20"
                            height="20"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10.3333 16.9997H11.6666V11.6663H16.9999V10.333H11.6666V4.99967H10.3333V10.333H4.99992V11.6663H10.3333V16.9997ZM0.333252 21.6663V0.333008H21.6666V21.6663H0.333252ZM1.66659 20.333H20.3333V1.66634H1.66659V20.333Z"
                                fill="#c80000"
                            />
                        </svg>
                            {t("updateQuestion")}
                        </button>
                    }
                </div>
            </div>

            {/* Survey Title Section */}
            <div className="ques-survey-preview">
                <h3 className="ques-survey-header">
                    {survey
                        ? survey?.surveyTitle
                        : surveyformData?.surveyformData?.surveyTitle === ""
                            ? "Survey Title"
                            : surveyformData?.surveyTitle}
                </h3>
                <hr style={{ margin: "0rem 0rem 1.5rem 0rem" }}></hr>

                <div className="survey-scroll-container">
                    {sortedSurveyData.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="survey-section">
                            <div
                                className="survey-section-header"
                                onClick={() => toggleSection(sectionIndex)}
                            >
                                <h2 className="survey-sub-title">
                                    {section.section === ""
                                        ? t("untitled")
                                        : section.section}
                                </h2>
                                <span
                                    style={{
                                        fontSize: "20px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {openSections[sectionIndex] ? "−" : "+"}
                                </span>
                            </div>
                            {openSections[sectionIndex] && (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={(e) =>
                                        handleDragEnd(e, sectionIndex)
                                    }
                                >
                                    <SortableContext
                                        items={section.questions.map(
                                            (q) => q.id
                                        )}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {section.questions.map(
                                            (question, questionIndex) => (
                                                <SortableQuestion
                                                    key={question.id}
                                                    question={question}
                                                    index={questionIndex}
                                                    sectionIndex={sectionIndex}
                                                    renderQuestion={
                                                        renderQuestion
                                                    }
                                                />
                                            )
                                        )}
                                    </SortableContext>
                                </DndContext>
                            )}
                        </div>
                    ))}
                </div>

                <div className="fixed-bottom-buttons">
                    <button className="saveButton" onClick={handleSave}>
                        {t("save")}
                    </button>
                    <button className="cancelButton" onClick={handleCancel}>
                        {t("cancel")}
                    </button>
                </div>
            </div>

            {/* Reusable Modal Component */}
            <ModalComponent
                isOpen={showModal}
                onClose={handleCloseModal}
                modalTitle={t("deleteQuestion")}
                modalText={`${t("deleteQuestionConfirmation")} "${deleteQuestionText}" ?`}
                buttons={[
                    <button
                        key="confirm"
                        className="saveButton"
                        onClick={handleConfirmDelete}
                    >
                        {t("confirm")}
                    </button>,
                    <button
                        key="cancel"
                        className="cancelButton"
                        onClick={handleCloseModal}
                    >
                        {t("cancel")}
                    </button>,
                ]}
            />
        </div>
    );
};

export default QuestionPage;